/**
 * Rozpad znaku — ułożenie organów (odłamków) w stanach awarii.
 *
 * Symbolika: rdzeń to żywa sieć i Nexus, odłamki to organy systemu. Każdy
 * stan awarii opowiada, co stało się z relacją organów i sieci — i dlatego
 * rdzeń nigdy nie pęka ani nie odpływa:
 *
 * | wariant             | strona                  | obraz                                                        |
 * | ------------------- | ----------------------- | ------------------------------------------------------------ |
 * | `blad`              | błąd nieoczekiwany      | organy rozsypane wokół sieci                                 |
 * | `nie-znaleziono`    | 404                     | znak cały, jeden organ zabłądził                             |
 * | `polaczenie`        | brak sieci / serwera    | sieć przygasła poza zasięgiem, organy oddalone od niej       |
 * | `serwis`            | prace serwisowe         | organy wyjęte w porządku — rysunek złożeniowy, nie chaos     |
 * | `organ-niedostepny` | organ nie odpowiada     | znak cały, jeden organ uśpiony                               |
 *
 * Brak dostępu (403) NIE ma wariantu: system działa, to człowiek nie ma
 * uprawnień — rozsypany znak sugerowałby awarię, której nie ma.
 *
 * Dane, nie losowanie: te same liczby na serwerze i w przeglądarce, więc
 * hydratacja nie migocze, a test sprawdza granicę ruchu każdego stanu.
 */
import {
  GRANICA_RUCHU,
  ODLAMKI,
  OBRECZE_SYGNETU,
  POLE_OCHRONNE,
  SIATKA_SYGNETU,
  type ObreczOdlamka,
  type Odlamek,
  type WycinekPierscienia,
} from './geometria';

export type WariantRozpadu =
  | 'blad'
  | 'nie-znaleziono'
  | 'polaczenie'
  | 'serwis'
  | 'organ-niedostepny';

export interface UlozenieOdlamka {
  /** Odsunięcie od środka wzdłuż dwusiecznej (jednostki siatki 118). */
  readonly odsuniecie: number;
  /** Obrót wokół środka odłamka, w stopniach. */
  readonly obrot: number;
  /** Krycie: 1 albo `cien` (organ przygaszony). */
  readonly cien?: boolean;
  /** Organ uśpiony — powolne przygasanie zamiast dryfu. */
  readonly uspiony?: boolean;
  /** Amplituda dryfu wzdłuż dwusiecznej (ujemna = do środka). 0 = bez dryfu. */
  readonly dryf: number;
  /** Wychylenie kątowe w szczycie dryfu, w stopniach. */
  readonly dryfObrot: number;
}

export interface UlozenieRozpadu {
  /** Sieć przygaszona — poza zasięgiem (`polaczenie`). */
  readonly rdzenPrzygaszony: boolean;
  readonly odlamek: (o: Odlamek) => UlozenieOdlamka;
}

const SPOKOJ: UlozenieOdlamka = { odsuniecie: 0, obrot: 0, dryf: 0, dryfObrot: 0 };

/** Organ, który w wariancie `nie-znaleziono` zabłądził (drobny kawałek przy szczelinie 0°). */
export const ODLAMEK_ZABLAKANY = 'zewnetrzna-gora-3';
/** Organ, który w wariancie `organ-niedostepny` śpi (duży dolny łuk obręczy środkowej). */
export const ODLAMEK_USPIONY = 'srodkowa-2';

export const ROZPAD: Readonly<Record<WariantRozpadu, UlozenieRozpadu>> = {
  blad: {
    rdzenPrzygaszony: false,
    odlamek: (o) => ({
      odsuniecie: o.odsuniecie,
      obrot: o.obrot,
      dryf: 1.5,
      dryfObrot: 2 * Math.sign(o.obrot),
    }),
  },
  'nie-znaleziono': {
    rdzenPrzygaszony: false,
    odlamek: (o) =>
      o.id === ODLAMEK_ZABLAKANY ? { odsuniecie: 20, obrot: 16, dryf: 2, dryfObrot: 3 } : SPOKOJ,
  },
  polaczenie: {
    rdzenPrzygaszony: true,
    odlamek: (o) => ({
      odsuniecie: o.obrecz === 'srodkowa' ? 4 : 9,
      obrot: 0,
      dryf: 2,
      dryfObrot: 0,
    }),
  },
  serwis: {
    rdzenPrzygaszony: false,
    odlamek: (o) => ({
      odsuniecie: o.obrecz === 'srodkowa' ? 5 : 10,
      obrot: 0,
      dryf: -1.5,
      dryfObrot: 0,
    }),
  },
  'organ-niedostepny': {
    rdzenPrzygaszony: false,
    odlamek: (o) => (o.id === ODLAMEK_USPIONY ? { ...SPOKOJ, cien: true, uspiony: true } : SPOKOJ),
  },
};

/**
 * Kawałek do narysowania: jeden odłamek albo kilka sąsiednich odłamków
 * TEGO SAMEGO łuku w identycznym ułożeniu, scalonych w jeden wycinek.
 * Bez scalania nieruszeni sąsiedzi rysowaliby się dwiema ścieżkami o
 * wspólnej krawędzi, a wygładzanie zostawia na takiej krawędzi włosowy szew.
 */
export interface Kawalek extends WycinekPierscienia {
  readonly obrecz: ObreczOdlamka;
  readonly ulozenie: UlozenieOdlamka;
  /** Faza dryfu — z pierwszego odłamka kawałka. */
  readonly faza: number;
  /** Identyfikatory odłamków, z których kawałek powstał. */
  readonly odlamki: readonly string[];
}

const toSamo = (a: UlozenieOdlamka, b: UlozenieOdlamka): boolean =>
  a.odsuniecie === b.odsuniecie &&
  a.obrot === b.obrot &&
  a.dryf === b.dryf &&
  a.dryfObrot === b.dryfObrot &&
  Boolean(a.cien) === Boolean(b.cien) &&
  Boolean(a.uspiony) === Boolean(b.uspiony);

/** Kawałki wariantu, po scaleniu nieruszonych sąsiadów w obrębie łuku. */
export function kawalkiRozpadu(wariant: WariantRozpadu): Kawalek[] {
  const u = ROZPAD[wariant];
  const wynik: Kawalek[] = [];
  for (const o of ODLAMKI) {
    const ulozenie = u.odlamek(o);
    const poprzedni = wynik[wynik.length - 1];
    // Scalanie tylko dla odłamków BEZ przesunięcia i obrotu: wspólne
    // przesunięcie wzdłuż WŁASNYCH dwusiecznych i tak by je rozdzieliło.
    const nieruszony = ulozenie.odsuniecie === 0 && ulozenie.obrot === 0;
    if (
      poprzedni &&
      nieruszony &&
      poprzedni.obrecz === o.obrecz &&
      poprzedni.do === o.od &&
      toSamo(poprzedni.ulozenie, ulozenie)
    ) {
      wynik[wynik.length - 1] = { ...poprzedni, do: o.do, odlamki: [...poprzedni.odlamki, o.id] };
    } else {
      wynik.push({
        id: o.id,
        obrecz: o.obrecz,
        r0: o.r0,
        r1: o.r1,
        od: o.od,
        do: o.do,
        ulozenie,
        faza: o.faza,
        odlamki: [o.id],
      });
    }
  }
  return wynik;
}

/**
 * Margines siatki wersji rozsypanej: pole ochronne znaku. Odłamki nie
 * wychodzą poza siatkę 118 powiększoną o ten margines (granica ruchu,
 * test), więc ilustracja ma stały rozmiar niezależnie od stanu.
 */
export const MARGINES_ROZPADU = POLE_OCHRONNE;
export const SIATKA_ROZPADU = SIATKA_SYGNETU + 2 * MARGINES_ROZPADU;

export { GRANICA_RUCHU, OBRECZE_SYGNETU };
